import _ from 'lodash';
import { transformSpectrumCoordinates } from 'common/utils/spectrumTransformations';
import { convertSpectrumYAxis } from 'utils/overlayUtils';
import { OVERLAY_TRACE_KEYS, pointHasTraceData } from 'utils/overlayTraceUtils';

/**
 * Pure overlay utilities extracted from ChartItem.
 * Keeps coordinate-system and trace-selection logic testable without the class component.
 */

export const toSpectrumOverlaySnapshot = points => (points || []).map(p => ({
  x: p.x,
  spectrum_data: p.spectrum_data,
  xUnit: p.xUnit,
  shaft_speed: p.shaft_speed
}));

export const getDefaultShaftSpeed = ({ selectedPoints, config, referenceShaftSpeed }) => {
  if (referenceShaftSpeed && referenceShaftSpeed > 0.001) {
    return referenceShaftSpeed;
  }

  if (Array.isArray(selectedPoints) && selectedPoints.length > 0) {
    const pointWithSpeed = selectedPoints.find(point =>
      point.shaft_speed && typeof point.shaft_speed === 'number' && !Number.isNaN(point.shaft_speed) && point.shaft_speed > 0
    );

    if (pointWithSpeed) {
      return pointWithSpeed.shaft_speed;
    }

    const pointWithSpectrumSpeed = selectedPoints.find(point =>
      point.spectrum &&
      point.spectrum.shaft_speed &&
      typeof point.spectrum.shaft_speed === 'number' &&
      !Number.isNaN(point.spectrum.shaft_speed) &&
      point.spectrum.shaft_speed > 0
    );

    if (pointWithSpectrumSpeed) {
      return pointWithSpectrumSpeed.spectrum.shaft_speed;
    }
  }

  const configSpeed =
    (config.spectrum && config.spectrum.shaft_speed) ||
    (config.spectrum && config.spectrum.metadata && config.spectrum.metadata.shaft_speed) ||
    (config.shaft_speed);

  return (configSpeed && configSpeed > 0.001) ? configSpeed : 7.5;
};

export const getResolvedSpectrumXUnit = ({
  showOverlay,
  selectedPoints,
  config,
  spectrumXUnitProp,
  stateSpectrumXUnit
}) => {
  if (showOverlay && Array.isArray(selectedPoints) && selectedPoints.length > 0) {
    const p = selectedPoints.find(pt => pt && pt.xUnit);
    if (p) return p.xUnit;
  }
  return stateSpectrumXUnit || spectrumXUnitProp || (config && config.spectrum && config.spectrum.freq_units) || 'Hz';
};

export const applyDisplayAmplitudeToSpectrum = (pristine, backendAmpUnits, displayYUnit) => {
  if (!pristine || !backendAmpUnits || !displayYUnit) return pristine;
  return convertSpectrumYAxis(pristine, backendAmpUnits, displayYUnit);
};

export const hasZeroSpeedOverlayPoint = (selectedPoints, config) =>
  selectedPoints.some((point) => {
    if (point.shaft_speed === 0) return true;
    if (point.spectrum && point.spectrum.shaft_speed === 0) return true;
    if (point.x && config && config.spectrum && config.spectrum.shaft_speed === 0) return true;
    return false;
  });

export const allOverlayPointsHaveValidSpeed = (selectedPoints, config) =>
  selectedPoints.every((point) => {
    const pointSpeed = point.shaft_speed ||
      (point.spectrum && point.spectrum.shaft_speed) ||
      (config && config.spectrum && config.spectrum.shaft_speed);
    return typeof pointSpeed === 'number' && !Number.isNaN(pointSpeed) && pointSpeed > 0;
  });

export const computeMultipleSpeedMarkers = ({ showOverlay, config, filteredPoints, currentXUnit }) => {
  if (!showOverlay || !Array.isArray(filteredPoints) || filteredPoints.length === 0) {
    return [];
  }

  const speedsData = filteredPoints
    .map((point, index) => {
      const speed = point.shaft_speed ||
        (point.spectrum && point.spectrum.shaft_speed) ||
        (config && config.spectrum && config.spectrum.shaft_speed) ||
        0;

      const markerInfo = currentXUnit === 'Orders' ? {
        markers: [
          { name: '1M', order: 1 },
          { name: '2M', order: 2 },
          { name: '3M', order: 3 },
          { name: '2LF', order: 2 }
        ].map(marker => ({
          ...marker,
          frequency: marker.order * speed,
          color: point.color,
          pointIndex: index
        }))
      } : null;

      return {
        index,
        speed,
        timestamp: point.x || point.timestamp,
        color: point.color,
        markerInfo,
        originalPoint: point
      };
    })
    .filter(item => item.speed > 0);

  return speedsData.length > 0 ? speedsData : [];
};

export const buildSpectrumFeaturesWithOverlayMarkers = ({
  config,
  tag,
  showOverlay,
  multipleSpeedMarkers,
  spectrumXUnit
}) => {
  const features = config.spectrumFeatures ? _.cloneDeep(config.spectrumFeatures) : { items: [] };

  if (tag && tag.forcingFrequencies) {
    features.items = features.items.concat(_.cloneDeep(tag.forcingFrequencies.items));
  }

  if (showOverlay && spectrumXUnit === 'Orders' && multipleSpeedMarkers && multipleSpeedMarkers.length > 0) {
    const adjustedMarkers = [];

    multipleSpeedMarkers.forEach((speedData) => {
      if (speedData.markerInfo && speedData.markerInfo.markers) {
        speedData.markerInfo.markers.forEach((marker) => {
          const pointIndex = marker.pointIndex !== undefined ? marker.pointIndex : speedData.index;
          const markerName = marker.name || marker.marker;
          const is1xMarker = markerName === '1M' || markerName === '1x' || markerName === '1X';
          const markerFrequency = is1xMarker ? speedData.speed : marker.frequency;

          adjustedMarkers.push({
            description: `${markerName} (${speedData.speed.toFixed(2)} Hz)`,
            frequency: markerFrequency,
            freq_units: 'Hz',
            marker: markerName,
            color: speedData.color,
            pointIndex,
            timestamp: speedData.timestamp,
            originalPointIndex: speedData.index
          });
        });
      }
    });

    features.items = features.items.filter(item =>
      !['1M', '2M', '3M', '2LF'].includes(item.marker)
    ).concat(adjustedMarkers);
  }

  return features;
};

export const getOverlaySpectrumData = ({ showOverlay, displayPoints, fallbackSpectrumData }) => {
  if (showOverlay && displayPoints && displayPoints.length > 0) {
    const validPoints = displayPoints.filter(point =>
      pointHasTraceData(point, OVERLAY_TRACE_KEYS.SPECTRUM)
    );
    if (validPoints.length > 0) {
      return validPoints.map(point => point.spectrum_data);
    }
  }
  return fallbackSpectrumData;
};

export const getOverlayWaveformData = ({ showOverlay, displayPoints, fallbackWaveformData }) => {
  if (showOverlay && displayPoints && displayPoints.length > 0) {
    const validPoints = displayPoints.filter(point =>
      pointHasTraceData(point, OVERLAY_TRACE_KEYS.WAVEFORM)
    );
    if (validPoints.length > 0) {
      return validPoints.map(point => point.waveform_data);
    }
  }
  return fallbackWaveformData;
};

export const getOverlayChartColors = ({
  showOverlay,
  defaultColor,
  displayPoints,
  requiresTraceData,
  dataKey
}) => {
  if (!showOverlay) {
    return [defaultColor];
  }

  if (displayPoints.length === 0) {
    return [defaultColor];
  }

  const validPoints = requiresTraceData
    ? displayPoints.filter(point => point[dataKey] && point[dataKey].length > 0)
    : displayPoints;

  return validPoints.length > 0
    ? validPoints.map(point => point.color)
    : [defaultColor];
};

export const getOverlayChartActiveData = ({
  showOverlay,
  displayPoints,
  requiresTraceData,
  dataKey
}) => {
  if (!showOverlay) {
    return [true];
  }

  if (displayPoints.length === 0) {
    return [true];
  }

  const validPoints = requiresTraceData
    ? displayPoints.filter(point => point[dataKey] && point[dataKey].length > 0)
    : displayPoints;

  return validPoints.map(() => true);
};

export const buildPointsRestoredToOriginalSpectrumUnit = ({
  selectedPoints,
  originalSpectrumUnit
}) =>
  selectedPoints.map((point) => {
    const updatedPoint = {
      ...point,
      xUnit: originalSpectrumUnit
    };

    if (point.originalData) {
      let convertedData = _.cloneDeep(point.originalData);

      const originalYUnit = (point.originalResponse && point.originalResponse.amp_units) || point.yUnit;
      const currentYUnit = point.yUnit;

      if (currentYUnit && originalYUnit && currentYUnit !== originalYUnit) {
        convertedData = convertSpectrumYAxis(convertedData, originalYUnit, currentYUnit);
      }

      updatedPoint.spectrum_data = convertedData;
    }

    if (point.originalFeatures) {
      updatedPoint.spectrumFeatures = _.cloneDeep(point.originalFeatures);
    }

    return updatedPoint;
  });

export const transformSpectrumForOverlayXAxis = ({
  showOverlay,
  selectedPoints,
  config,
  currentXUnit,
  spectrumData,
  originalData,
  referenceShaftSpeed
}) => {
  const originalSpectrumUnit = (config && config.spectrum && config.spectrum.freq_units) || 'Hz';

  if (!showOverlay || !Array.isArray(selectedPoints) || selectedPoints.length === 0) {
    return null;
  }

  const shaftSpeed = getDefaultShaftSpeed({
    selectedPoints,
    config,
    referenceShaftSpeed
  });

  return transformSpectrumCoordinates({
    sourceUnit: originalSpectrumUnit,
    targetUnit: currentXUnit,
    shaftSpeed,
    shaftSpeedUnit: originalSpectrumUnit,
    spectrumData,
    originalData,
    originalDataUnit: originalSpectrumUnit,
    features: config.spectrumFeatures || config.spectrum.features,
    originalFeatures: config.originalSpectrumFeatures || config.spectrumFeatures || config.spectrum.features,
    originalFeaturesUnit: originalSpectrumUnit,
    selectedPoints
  });
};
